import React, { useEffect, useCallback, useState, useRef } from "react";
import "./EvMeasurementLineChart.css";
import config from "../../config";
import { Line } from "react-chartjs-2";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useDispatch, useSelector } from "react-redux";
import { fetchEv_list } from "../../store";

import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { ko } from "date-fns/locale";
import {  Card, Space } from "antd";
import moment from "moment";
import { CSVLink } from "react-csv";

ChartJS.register(
  Tooltip,
  Legend,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title
);

const EvMeasurementLineChart = ({ chartWidth = 800, chartHeight = 330 }) => {
  const [chartData, setChartData] = useState({
    outdoorTemp: [],
    horizontalIrradiance: [],
    surfaceTemp: [],
    tiltedIrradiance: [],
  });

  const upperChartRef = useRef(null);
  const lowerChartRef = useRef(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const selectedDateStr = selectedDate.toISOString().split('T')[0];
  const dispatch = useDispatch();
  const Ev_list = useSelector((state) => state.Ev_list || {});

  const generateTimeLabels = (baseDate) => {
    const labels = [];
    const base = moment(baseDate).startOf("day");
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j += 10) {
        labels.push(
          base.clone().add(i, "hours").add(j, "minutes").toDate()
        );
      }
    }
    return labels;
  };

  const findClosestTimeData = (data, targetTime) => {
    // targetTime은 선택된 날짜의 로컬 시간(KST) 기준
    const targetDate = moment(targetTime).format('YYYY-MM-DD');
    const targetMinutes = targetTime.getHours() * 60 + targetTime.getMinutes();

    return data.reduce((closest, item) => {
      const createdAt = new Date(item.created_at);
      // 서버 데이터를 로컬 시간으로 변환
      // created_at이 UTC 문자열이면 자동으로 로컬 시간으로 변환됨
      const itemDate = moment(createdAt).format('YYYY-MM-DD');
      const itemMinutes = createdAt.getHours() * 60 + createdAt.getMinutes();
      
      // 같은 날짜인지 확인 (날짜가 다르면 제외)
      if (itemDate !== targetDate) {
        return closest;
      }
      
      const diff = Math.abs(itemMinutes - targetMinutes);

      return diff <= 3 &&
        (!closest ||
          diff <
            Math.abs(
              new Date(closest.created_at).getHours() * 60 +
                new Date(closest.created_at).getMinutes() -
                targetMinutes
            ))
        ? item
        : closest;
    }, null);
  };

  const processSeqData = (data) => {
    const seqData = {};
    data.forEach((item) => {
      if (!seqData[item.seq]) {
        seqData[item.seq] = [];
      }
      seqData[item.seq].push(item);
    });
    return seqData;
  };

  const fetchData = useCallback(async () => {
    try {
      const selectedDateInKST = moment(selectedDate).format('YYYY-MM-DD');

      const response = await fetch(
        `${config.backendUrl}/get_hourly_Ev_mrt_data`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ date: selectedDateInKST }),
        }
      );

      const result = await response.json();
      if (!result || !result.data) {
        console.error("No data returned from the server.");
        setChartData({
          outdoorTemp: Array(144).fill(null),
          horizontalIrradiance: Array(144).fill(null),
          surfaceTemp: Array(144).fill(null),
          tiltedIrradiance: Array(144).fill(null),
        });
        return;
      }

      const seqData = processSeqData(result.data);
      const timeLabels = generateTimeLabels(selectedDate);
      const outdoorTemp = [];
      const horizontalIrradiance = [];
      const surfaceTemp = [];
      const tiltedIrradiance = [];

      const seqs = Ev_list.seq || [];
      const sunTypes = Ev_list.ev_sunType || [];
      const tempTypes = Ev_list.ev_temperatureType || [];
      const hasTypeMapping = seqs.length > 0 && sunTypes.length > 0 && tempTypes.length > 0;

      const seqKeys = Object.keys(seqData);
      const seq1 = seqKeys[0];
      const seq2 = seqKeys[1];

      timeLabels.forEach((label) => {
        if (hasTypeMapping) {
          let horizontalVal = null;
          let tiltedVal = null;
          let surfaceVal = null;
          let outdoorVal = null;

          seqs.forEach((seq, index) => {
            const dataPoint = findClosestTimeData(seqData[seq], label);
            if (!dataPoint) return;

            const sunType = sunTypes[index];
            const tempType = tempTypes[index];

            if (sunType === 1 && horizontalVal === null) {
              horizontalVal = dataPoint.sun;
            }
            if (sunType === 0 && tiltedVal === null) {
              tiltedVal = dataPoint.sun;
            }
            if (tempType === 0 && surfaceVal === null) {
              surfaceVal = dataPoint.temperature / 10;
            }
            if (tempType === 1 && outdoorVal === null) {
              outdoorVal = dataPoint.temperature / 10;
            }
          });

          horizontalIrradiance.push(horizontalVal);
          tiltedIrradiance.push(tiltedVal);
          surfaceTemp.push(surfaceVal);
          outdoorTemp.push(outdoorVal);
        } else {
          const data1 = seq1 ? findClosestTimeData(seqData[seq1], label) : null;
          const data2 = seq2 ? findClosestTimeData(seqData[seq2], label) : null;

          horizontalIrradiance.push(data1 ? data1.sun : null);
          outdoorTemp.push(data1 ? data1.temperature / 10 : null);
          tiltedIrradiance.push(data2 ? data2.sun : null);
          surfaceTemp.push(data2 ? data2.temperature / 10 : null);
        }
      });

      setChartData({
        outdoorTemp,
        horizontalIrradiance,
        surfaceTemp,
        tiltedIrradiance,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      setChartData({
        outdoorTemp: Array(144).fill(null),
        horizontalIrradiance: Array(144).fill(null),
        surfaceTemp: Array(144).fill(null),
        tiltedIrradiance: Array(144).fill(null),
      });
    }
  }, [selectedDate, Ev_list]);

  useEffect(() => {
    dispatch(fetchEv_list());
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const generateCSVData = () => {
    const csvData = [];
    generateTimeLabels(selectedDate).forEach((label, index) => {
      csvData.push({
        Time: moment(label).format("HH:mm"),
        OutdoorTemp: chartData.outdoorTemp[index],
        HorizontalIrradiance: chartData.horizontalIrradiance[index],
        SurfaceTemp: chartData.surfaceTemp[index],
        TiltedIrradiance: chartData.tiltedIrradiance[index],
      });
    });
    return csvData;
  };

  const csvHeaders = [
    { label: "Time", key: "Time" },
    { label: "Outdoor Temperature (°C)", key: "OutdoorTemp" },
    { label: "Horizontal Irradiance (W/m²)", key: "HorizontalIrradiance" },
    { label: "Surface Temperature (°C)", key: "SurfaceTemp" },
    { label: "Tilted Irradiance (W/m²)", key: "TiltedIrradiance" },
  ];

  const upperChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
          font: {
            size: 12,
            weight: "bold",
          },
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        titleColor: "#333",
        bodyColor: "#666",
        borderColor: "#ccc",
        borderWidth: 1,
        padding: 10,
        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(1);
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: "hour",
          displayFormats: {
            hour: "HH:mm",
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 10,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          font: {
            size: 10,
          },
          callback: function (value, index, values) {
            return value;
          },
        },
        title: {
          display: true,
          text: "W/m²",
          font: {
            size: 12,
            weight: "bold",
          },
          padding: { top: 0, bottom: 10 },
          position: "left",
        },
      },
    },
  };

  const lowerChartOptions = {
    ...upperChartOptions,
    scales: {
      ...upperChartOptions.scales,
      y: {
        ...upperChartOptions.scales.y,
        title: {
          display: true,
          text: "°C",
          font: {
            size: 12,
            weight: "bold",
          },
          padding: { top: 0, bottom: 10 },
          position: "left",
        },
        ticks: {
          ...upperChartOptions.scales.y.ticks,
          callback: function (value, index, values) {
            return value;
          },
        },
      },
    },
  };

  const upperData = {
    labels: generateTimeLabels(selectedDate),
    datasets: [
      {
        label: "수평 일사량",
        data: chartData.horizontalIrradiance,
        borderColor: "rgba(255, 99, 132, 1)",
        backgroundColor: "rgba(255, 99, 132, 0.2)",
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 3,
        font: {
          size: 8,
        },
      },
      {
        label: "경사 일사량",
        data: chartData.tiltedIrradiance,
        borderColor: "rgba(54, 162, 235, 1)",
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 3,
        font: {
          size: 8,
        },
      },
    ],
  };

  const lowerData = {
    labels: generateTimeLabels(selectedDate),
    datasets: [
      {
        label: "외기 온도",
        data: chartData.outdoorTemp,
        borderColor: "rgba(255, 206, 86, 1)",
        backgroundColor: "rgba(255, 206, 86, 0.2)",
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: "표면 온도",
        data: chartData.surfaceTemp,
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
    ],
  };


  return (
    <Card
      style={{
        margin: "5px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        maxWidth: "1580px",
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space>
        <DatePicker
          selected={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          dateFormat="yyyy-MM-dd"
          locale={ko}
          className="styled-datepicker"
          popperPlacement="bottom"
          maxDate={new Date()}
          customInput={
            <input
              style={{
                textAlign: 'center',
                height: '25px',
                lineHeight: '25px',
                width: '150px',
                marginRight: '10px',
              }}
            />
          }
        />
          <CSVLink
            data={generateCSVData()}
            headers={csvHeaders}
            filename={`data_${selectedDateStr}.csv`}
            className="styled-button"
            style={{
              marginLeft: '20px',
              height: '25px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              backgroundColor: '#1D6C41',
            }}
          >
            CSV 다운로드
          </CSVLink>
        </Space>

        <Card>
          <div style={{ height: "315px", marginTop: "-30px" }}>
            <Line
              ref={upperChartRef}
              data={upperData}
              options={upperChartOptions}
            />
          </div>
        </Card>

        <Card>
          <div style={{ height: "315px", marginTop: "-30px" }}>
            <Line
              ref={lowerChartRef}
              data={lowerData}
              options={lowerChartOptions}
            />
          </div>
        </Card>
      </Space>
    </Card>
  );
};

export default EvMeasurementLineChart;
